"use client";

/**
 * O rasto das últimas acções antes do erro.
 *
 * Um stack trace diz ONDE rebentou. Não diz o que a pessoa fez para lá chegar,
 * e é quase sempre isso que falta para reproduzir. Este módulo mantém um anel
 * das últimas acções e entrega-o com o report.
 *
 * ─── A regra de privacidade, e o que ela NÃO garante ──────────────────────
 *
 * Esta app trata dados de leads: nomes, telefones, e-mails, NIFs. Um registo
 * de bugs que os aspirasse passava a ser uma segunda base de dados pessoais,
 * sem nenhuma das cautelas da primeira e sem ninguém ter decidido criá-la.
 *
 * Por isso:
 *
 *   1. NUNCA se lê o valor de um campo. Nem `input.value`, nem `textarea`,
 *      nem o conteúdo de um `contenteditable`. Não há caminho no código que
 *      lhes toque — é a única garantia verdadeiramente sólida aqui.
 *
 *   2. O rótulo de um elemento é procurado por ordem de segurança:
 *      `data-bug-label` (escrito por nós, de confiança) → `data-testid` →
 *      `aria-label` → texto do botão. Só as duas primeiras são garantidamente
 *      isentas de dados de pessoas.
 *
 *   3. O que passa é limpo de tudo o que tem FORMA reconhecível: e-mails,
 *      telefones, NIFs, IBANs, cartões. Ver `scrubLabel`.
 *
 * O que isto NÃO resolve: **um nome próprio não tem forma reconhecível**.
 * Um botão que diga "Abrir ficha de João Silva" passa os três filtros, porque
 * nenhum padrão distingue "João Silva" de "Novo Contacto". Se isso incomodar,
 * a saída é marcar esses elementos com `data-bug-skip` — ou desligar de todo a
 * captura de texto em `CAPTURE_TEXT_LABELS`.
 */

export type BreadcrumbType = "nav" | "click" | "http" | "info";

export interface Breadcrumb {
  t: BreadcrumbType;
  /** ms desde o arranque da página — relativo de propósito, ver `record`. */
  at: number;
  label: string;
}

/**
 * Vinte é o suficiente para reconstituir o caminho e pouco o bastante para o
 * report caber num pedido pequeno — que importa, porque este pedido é enviado
 * exactamente quando a app está partida.
 */
const MAX_BREADCRUMBS = 20;

/**
 * Interruptor único para o ponto 3 acima. A `false`, os rótulos passam a ser
 * só estruturais (`button[data-testid=guardar-lead]`, `a`, `div[role=button]`)
 * — ilegíveis mas incapazes de transportar o nome de quem quer que seja.
 */
const CAPTURE_TEXT_LABELS = true;

const MAX_LABEL = 80;

const ring: Breadcrumb[] = [];
const startedAt = Date.now();

/**
 * Padrões com forma própria. Nomes de pessoas não estão aqui porque não podem
 * estar — ver o comentário do topo.
 */
const PII_PATTERNS: Array<[RegExp, string]> = [
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, "<email>"],
  // IBAN português e estrangeiro.
  [/\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g, "<iban>"],
  // Cartão: 13 a 19 dígitos, com ou sem separadores.
  [/\b(?:\d[ -]?){13,19}\b/g, "<cartao>"],
  // Telefone PT com ou sem indicativo, e NIF (9 dígitos).
  //
  // Sem `\b` à cabeça: antes de um `+` nunca há limite de palavra (os dois
  // lados são caracteres não-palavra), portanto um `\b` inicial fazia o grupo
  // do indicativo nunca casar e deixava `+351` de fora do que era apagado.
  // O `\b` que interessa é o de antes dos nove dígitos, e esse mantém-se.
  [/(?:\+351[\s-]?)?\b\d{9}\b/g, "<num>"],
  // Qualquer outra corrida longa de dígitos.
  [/\b\d{6,}\b/g, "<num>"],
];

export function scrubLabel(raw: string): string {
  let out = raw.replace(/\s+/g, " ").trim();
  for (const [pattern, replacement] of PII_PATTERNS) {
    out = out.replace(pattern, replacement);
  }
  return out.slice(0, MAX_LABEL);
}

/**
 * Instante relativo ao arranque da página, não a data absoluta.
 *
 * "há 3 segundos" é o que interessa para reconstituir uma sequência, e um
 * relógio relativo não diz a que horas é que uma pessoa em concreto esteve a
 * trabalhar — informação que este registo não tem razão nenhuma para guardar.
 */
export function record(type: BreadcrumbType, label: string): void {
  ring.push({ t: type, at: Date.now() - startedAt, label: scrubLabel(label) });
  if (ring.length > MAX_BREADCRUMBS) ring.shift();
}

/** Cópia — quem lê o rasto não deve poder alterá-lo. */
export function getBreadcrumbs(): Breadcrumb[] {
  return ring.slice();
}

export function clearBreadcrumbs(): void {
  ring.length = 0;
}

/**
 * Descreve um elemento sem lhe ler o conteúdo.
 *
 * Devolve `null` quando o elemento (ou um antepassado) está marcado com
 * `data-bug-skip` — a válvula de escape para zonas que mostram dados de
 * pessoas.
 */
function describeElement(el: Element): string | null {
  if (el.closest("[data-bug-skip]")) return null;

  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role");
  const structural = role ? `${tag}[role=${role}]` : tag;

  const trusted =
    el.getAttribute("data-bug-label") ?? el.getAttribute("data-testid");
  if (trusted) return `${structural} "${trusted}"`;

  if (!CAPTURE_TEXT_LABELS) return structural;

  const aria = el.getAttribute("aria-label");
  if (aria) return `${structural} "${aria}"`;

  // Só de controlos, e só o começo: o texto de um `<div>` qualquer pode ser
  // uma tabela inteira de leads.
  if (tag === "button" || tag === "a" || role === "button") {
    const text = (el.textContent ?? "").replace(/\s+/g, " ").trim();
    if (text) return `${structural} "${text.slice(0, 40)}"`;
  }

  return structural;
}

let installed = false;

/**
 * Liga a captura. Idempotente: chamado do provider que monta uma vez.
 */
export function installBreadcrumbs(): () => void {
  if (installed || typeof window === "undefined") return () => {};
  installed = true;

  const onClick = (ev: MouseEvent) => {
    const target = ev.target;
    if (!(target instanceof Element)) return;

    // Sobe até ao controlo mais próximo: o clique cai quase sempre num
    // `<span>` lá dentro, e "span" não diz nada a ninguém.
    const control =
      target.closest("button, a, [role=button], [role=menuitem], [role=tab]") ??
      target;

    const described = describeElement(control);
    if (described) record("click", described);
  };

  const onNav = () => record("nav", window.location.pathname);

  // `capture` para apanhar o clique mesmo que alguém chame stopPropagation
  // a meio — e é justamente nos sítios com lógica esquisita que os erros vivem.
  document.addEventListener("click", onClick, { capture: true, passive: true });
  window.addEventListener("popstate", onNav);

  record("nav", window.location.pathname);

  return () => {
    document.removeEventListener("click", onClick, { capture: true });
    window.removeEventListener("popstate", onNav);
    installed = false;
  };
}
